import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Box, IconButton, Tooltip } from "@mui/material";
import { Visibility } from "@mui/icons-material";
import dataDogIcon from "./assets/datadog.png";
import DatadogAlertModal from "../modals/dataDogAlertDetailsModal";
import { WidgetContainer } from "../shared/widgetContainer/widgetContainer";
import { DownloadExcel } from "../../functions/downloadExcel";
import DogChasingBallLoader from "../shared/DogChasingBallLoader/DogChasingBallLoader";

dayjs.extend(relativeTime);

const DatadogAlertsTable = ({ data = [], updatedAt }) => {
  const priorityColors = {
    1: "#BA1650", // Red for P1 (Critical)
    2: "#E97F03", // Orange for P2 (High)
    3: "#70C930", // Green for P3 (Low)
  };
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showLoader, setShowLoader] = useState(true);

  // Show the loader for at least 3 seconds to see the animation
  useEffect(() => {
    if (data && data.length > 0) {
      const timer = setTimeout(() => {
        setShowLoader(false);
      }, 1000); // 3 seconds delay

      return () => clearTimeout(timer);
    } else {
      setShowLoader(true);
    }
  }, [data]);

  return (
    <WidgetContainer
      data={data}
      loaded={true}
      title="DataDog (P1 & P2) Alerts"
      description="View all DataDog alerts with priority 1 and 2"
      maximize={true}
      download={true}
      onDownload={() => {
        DownloadExcel("DataDogAlerts", data);
      }}
    >
      {data && data.length > 0 && !showLoader ? (
        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            minHeight: 0,
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              height: "100%",
              maxHeight: "530px",
            }}
          >
            {/* Fixed Header */}
            <thead
              style={{
                position: "sticky",
                top: 0,
                background: "#505050",
                zIndex: 10,
              }}
            >
              <tr>
                <th
                  style={{
                    ...headerStyle,
                    textAlign: "center",

                    width: "50px",
                  }}
                >
                  Severity
                </th>
                <th
                  style={{
                    ...headerStyle,
                    textAlign: "center",
                    width: "50px",
                  }}
                >
                  Priority
                </th>

                <th
                  style={{
                    ...headerStyle,
                    textAlign: "center",
                    width: "60px",
                  }}
                >
                  Environment
                </th>

                <th style={headerStyle}>Alert Name</th>

                <th
                  style={{
                    ...headerStyle,
                    textAlign: "center",
                    width: "120px",
                  }}
                >
                  Triggered
                </th>

                <th
                  style={{
                    ...headerStyle,
                    textAlign: "center",
                    width: "100px",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>

            {/* Scrollable Table Body */}
            <tbody>
              {data &&
                data.map((alert) => (
                  <tr key={alert.id} style={{ borderBottom: "1px solid #636364" }}>
                    <td
                      style={{
                        ...cellStyle,
                        backgroundColor: alert.statusColor,
                        fontWeight: "bold",
                        textAlign: "center",
                        textTransform: "uppercase",
                        border: "1px solid #9E9B9F",
                      }}
                    >
                      {alert.statusColor === "#BA1650"
                        ? "Critical"
                        : alert.statusColor === "#E97F03"
                          ? "Warning"
                          : "Low"}
                    </td>
                    <td
                      style={{
                        ...cellStyle,
                        fontWeight: "bold",
                        backgroundColor: priorityColors[alert.priority] || "#9D419B",
                        color: "white",
                        textAlign: "center",
                      }}
                    >
                      P{alert.priority}
                    </td>
                    <td
                      style={{
                        ...cellStyle,
                        textAlign: "center",
                        border: "1px solid #9E9B9F",
                      }}
                    >
                      {alert.environment}
                    </td>
                    <td
                      style={{
                        ...cellStyle,
                        flex: 1,
                        maxWidth: "1px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {alert.name}
                    </td>
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      {dayjs.unix(alert.last_triggered_ts).fromNow()}
                    </td>
                    <td style={{ ...cellStyle, textAlign: "center" }}>
                      <Tooltip title="View Details">
                        <IconButton
                          onClick={() => {
                            setSelectedAlert(alert);
                            setIsModalOpen(true);
                          }}
                          sx={{ color: "#fff", padding: 0 }}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Open in Datadog">
                        <IconButton
                          sx={{ padding: 0, marginLeft: "10px" }}
                          onClick={() => {
                            window.open(alert.link, "_blank", "noopener,noreferrer");
                          }}
                        >
                          <img
                            src={dataDogIcon}
                            alt="Open Alert"
                            style={{ width: 24, height: 24 }}
                          />
                        </IconButton>
                      </Tooltip>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
          <DatadogAlertModal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            alert={selectedAlert}
          />
        </Box>
      ) : (
        <DogChasingBallLoader message="Fetching DataDog alerts..." />
      )}
    </WidgetContainer>
  );
};

// ** Styles **
const headerStyle = {
  padding: "8px",
  textAlign: "left",
  color: "#fff",
  borderBottom: "1px solid #fff",
  backgroundColor: "#505050",
  textTransform: "uppercase",
  fontWeight: "bold",
  fontSize: "14px",
  height: "40px",
  cursor: "pointer",
  maxWidth: "200px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const cellStyle = {
  padding: "8px",
  textAlign: "left",
  color: "#eee",
  borderBottom: "1px solid #9E9B9F",
  overflow: "hidden",
  fontSize: "13px",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
};

export default DatadogAlertsTable;
